// Sub-Step 3.3 Phase 2 — useViewTracking Hook.
//
// Schickt EINEN POST /user-scrollys/:pk/view sobald der Leser 25 % des
// Scrollys gescrollt hat. Pro Tab-Session (sessionStorage) nur einmal —
// kein Re-Scroll-Doppel-Post.
//
// Backend: @Public, mit Body { sessionId, readPercent }. Backend
// inkrementiert views-Counter nur wenn readPercent >= 0.25, plus
// dedupliziert via (publicKey, sessionId) — Frontend-side dedup ist
// also defense-in-depth (spart Backend-Roundtrip), nicht Korrektheit.
//
// Anonymous + authenticated funktionieren gleich — keine Token-Pflicht
// auf diesem Endpoint (siehe engagement.controller.ts:46 `@Public()`).
//
// Fortschritts-Quelle (seit 2026-09-11): lib/reader-progress.ts, gespeist
// von ScrollyViewer.onScrollProgress ueber ViewerChrome. Vorher mass der
// Hook window.scrollY — im Container-Scroll des Readers immer 0 bzw. bei
// nicht scrollbarem Dokument sofort 1 → der View-Ping feuerte beim Mount
// und views waren Seitenaufrufe statt 25-%-Leser (Prod-Befund WK).
//
// useEffect-Audit (Phase-1-Lesson):
//   - Cleanup beim Unmount (Abo abmelden)
//   - useRef-Guards: `triggeredRef` verhindert mehrfaches POST nach Threshold,
//     `sessionIdRef` cached die sessionId für ganze Lifetime
//   - Dependency-Array: [publicKey, threshold, disabled] — wenn sich der
//     publicKey ändert (Page-Wechsel), wird die ganze Subscription neu
//     aufgesetzt mit eigener triggeredRef

import { useEffect, useRef } from "react";
import {
  readReaderProgress,
  subscribeReaderProgress,
} from "../lib/reader-progress";

const VIEW_THRESHOLD = 0.25; // 25 % des Scrollys gescrollt
const STORAGE_KEY_PREFIX = "scrolly:view-tracked:";
const SESSION_ID_KEY = "scrolly:view-session-id";

/**
 * Holt oder generiert eine Tab-Session-ID via sessionStorage.
 * Geteilt zwischen allen scrolly-Views in dieser Tab-Session.
 * crypto.randomUUID() ist Standard in modernen Browsern (>2022).
 */
export function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    // SafariPrivateMode-Fallback: in-memory ID, lokal nur für diesen Mount.
    return `mem-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Tracking-Dedup-Check: schon mal getrackt in dieser Session?
 */
function hasTrackedThisSession(publicKey: string): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY_PREFIX + publicKey) === "1";
  } catch {
    return false;
  }
}

function markTrackedThisSession(publicKey: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + publicKey, "1");
  } catch {
    // Silent fallback — in-memory dedup übernimmt via triggeredRef
  }
}

interface UseViewTrackingProps {
  /** publicKey des Scrollys. Wenn leer/undefined: kein Tracking. */
  publicKey: string | undefined | null;
  /** Optional: Override-Threshold (Default 0.25). */
  threshold?: number;
  /** Disable-Flag für Test-Stubs / OG-Render / Embed. */
  disabled?: boolean;
}

/**
 * Hook: triggert genau einen POST /user-scrollys/:pk/view sobald der
 * Renderer-Fortschritt die Threshold-Marke (default 25 %) überschreitet.
 *
 * @example
 * ```tsx
 * // In der Viewer-Page-Komponente
 * useViewTracking({ publicKey: resolved.publicKey });
 * ```
 */
export function useViewTracking(props: UseViewTrackingProps): void {
  const { publicKey, threshold = VIEW_THRESHOLD, disabled = false } = props;
  // useRef-Guards (Phase-1-Lesson):
  // - triggeredRef: einmaliger POST pro Mount-Lifecycle
  // - sessionIdRef: cached, regenerate-prevention
  const triggeredRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (disabled) return;
    if (!publicKey) return;
    if (typeof window === "undefined") return;
    // Wenn schon in dieser Tab-Session getrackt → kein Abo, kein POST.
    if (hasTrackedThisSession(publicKey)) return;

    // Reset Refs für neuen publicKey (z.B. Client-Routing zu anderem Scrolly)
    triggeredRef.current = false;
    sessionIdRef.current ??= getOrCreateSessionId();

    const checkAndTrigger = (percent: number) => {
      if (triggeredRef.current) return;
      if (percent < threshold) return;
      // Threshold erreicht — sofort markieren um Re-Trigger zu verhindern.
      triggeredRef.current = true;
      markTrackedThisSession(publicKey);

      const body = JSON.stringify({
        sessionId: sessionIdRef.current,
        readPercent: percent,
      });
      // Fire-and-forget — kein .then() oder .catch() Logging
      // ist gewollt: Backend-Errors sollen User nicht beeinflussen.
      // Plus: navigator.sendBeacon wäre robuster gegen Page-Unload,
      // aber wir brauchen Content-Type: application/json was sendBeacon
      // nicht direkt unterstützt → fetch + keepalive.
      void fetch(
        `/user-scrollys/${encodeURIComponent(publicKey)}/view`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        },
      ).catch(() => {
        // Network-Error: nicht recovern. Counter wird beim nächsten
        // Reload neu berechnet (Backend liefert frische counts).
      });
    };

    // Initial-Check: der Renderer kann schon gemeldet haben (Hash-Anchor,
    // Re-Mount des Hooks). Sofort prüfen, nicht erst auf die nächste Meldung.
    checkAndTrigger(readReaderProgress(publicKey));
    if (triggeredRef.current) return; // bereits getriggert beim Initial-Check

    return subscribeReaderProgress(publicKey, checkAndTrigger);
  }, [publicKey, threshold, disabled]);
}
